if (!customElements.get('vip-delivery-scheduler')) {
  customElements.define(
    'vip-delivery-scheduler',
    class VipDeliveryScheduler extends HTMLElement {
      constructor() {
        super();
        this.config = JSON.parse(this.querySelector('[data-vip-delivery-config]').textContent);
        this.dateInput = this.querySelector('[data-vip-date-input]');
        this.windowInput = this.querySelector('[data-vip-window-input]');
        this.dateError = this.querySelector('[data-vip-date-error]');
        this.dateErrorText = this.querySelector('[data-vip-date-error-text]');
        this.windowError = this.querySelector('[data-vip-window-error]');
        this.sameDayNote = this.querySelector('[data-vip-same-day]');
        this.zipInput = this.querySelector('[data-vip-zip-input]');
        this.zipResult = this.querySelector('[data-vip-zip-result]');
        this.form = document.getElementById(this.dataset.formId);
      }

      connectedCallback() {
        this.setupDateBounds();
        this.prefillSavedDate();
        this.fetchCartSchedule();
        this.watchCartChanges();
        this.syncWindowAvailability();
        this.dateInput.addEventListener('change', () => {
          if (this.validateDate(true)) this.saveDate(this.dateInput.value);
          this.syncWindowAvailability();
        });
        this.windowInput.addEventListener('change', () => this.toggleError(this.windowError, false));

        if (this.form) {
          this.boundSubmit = this.onFormSubmit.bind(this);
          this.form.addEventListener('submit', this.boundSubmit, { capture: true });
        }

        this.conflictEl = this.querySelector('[data-vip-conflict]');
        if (this.conflictEl) {
          this.querySelector('[data-vip-conflict-adopt]').addEventListener('click', () => this.adoptCartSchedule());
        }

        if (this.zipInput) {
          this.querySelector('[data-vip-zip-check]').addEventListener('click', () => this.checkZip());
          this.zipInput.addEventListener('keydown', (event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              this.checkZip();
            }
          });
          const saved = this.readSavedZip();
          if (saved) {
            this.zipInput.value = saved;
            this.checkZip(true);
          }
        }
      }

      disconnectedCallback() {
        if (this.form && this.boundSubmit) {
          this.form.removeEventListener('submit', this.boundSubmit, { capture: true });
        }
        if (this.unsubscribeCart) this.unsubscribeCart();
        if (this.boundPageShow) window.removeEventListener('pageshow', this.boundPageShow);
      }

      /* The guard below can only be as fresh as the cart snapshot it
         holds. Two ordinary paths used to leave that snapshot stale:
         adding a second arrangement without reloading the page (the
         cart was empty when the page loaded, so the snapshot stayed
         null), and returning to a product with the back button, which
         restores the page from bfcache without re-running
         connectedCallback. Both let a second, conflicting schedule
         into the cart unchallenged. */
      watchCartChanges() {
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
          this.unsubscribeCart = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.fetchCartSchedule());
        }
        this.boundPageShow = (event) => {
          if (event.persisted) this.fetchCartSchedule();
        };
        window.addEventListener('pageshow', this.boundPageShow);
      }

      /* ---- timezone-correct "now" ---- */

      nowInStoreTz() {
        const parts = new Intl.DateTimeFormat('en-CA', {
          timeZone: this.config.timezone,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        }).formatToParts(new Date());
        const get = (type) => parts.find((p) => p.type === type).value;
        return {
          date: `${get('year')}-${get('month')}-${get('day')}`,
          minutes: parseInt(get('hour'), 10) * 60 + parseInt(get('minute'), 10),
        };
      }

      cutoffMinutes() {
        const [h, m] = this.config.cutoffTime.split(':').map(Number);
        return h * 60 + m;
      }

      addDays(isoDate, days) {
        const [y, m, d] = isoDate.split('-').map(Number);
        const date = new Date(Date.UTC(y, m - 1, d + days));
        return date.toISOString().split('T')[0];
      }

      isBlackout(isoDate) {
        return this.config.blackoutDates.includes(isoDate);
      }

      setupDateBounds() {
        const now = this.nowInStoreTz();
        this.sameDayOpen = this.config.sameDayEnabled && now.minutes < this.cutoffMinutes();

        let min = this.sameDayOpen ? now.date : this.addDays(now.date, 1);
        while (this.isBlackout(min)) min = this.addDays(min, 1);

        this.minDate = min;
        this.maxDate = this.addDays(now.date, this.config.maxLeadDays);
        this.dateInput.min = this.minDate;
        this.dateInput.max = this.maxDate;

        if (this.sameDayNote) {
          if (this.sameDayOpen && this.minDate === now.date) {
            const remaining = this.cutoffMinutes() - now.minutes;
            const hours = Math.floor(remaining / 60);
            const minutes = remaining % 60;
            this.sameDayNote.textContent = this.config.strings.sameDayAvailable
              .replace('[hours]', String(hours))
              .replace('[minutes]', String(minutes));
          } else {
            this.sameDayNote.textContent = this.config.strings.sameDayPassed;
          }
        }
      }


      /* ---- windows that have already elapsed today ----
         The cutoff governs whether same-day is offered at all; it says
         nothing about which of the day's windows are still reachable.
         Without this, an order placed at 2pm could be scheduled into
         today's 6:00-9:00 AM window — a delivery promise that was
         already impossible when the customer made it. Only today is
         affected; every future date keeps the full list. */

      clockToMinutes(value) {
        const match = String(value).trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?$/i);
        if (!match) return null;
        let hours = parseInt(match[1], 10);
        const minutes = parseInt(match[2], 10);
        const meridiem = match[3] && match[3].toUpperCase();
        if (meridiem === 'PM' && hours !== 12) hours += 12;
        if (meridiem === 'AM' && hours === 12) hours = 0;
        return hours * 60 + minutes;
      }

      windowEndMinutes(label) {
        const parts = String(label).split('-');
        if (parts.length < 2) return null;
        const end = this.clockToMinutes(parts.pop());
        const start = this.clockToMinutes(parts.pop());
        if (end === null) return null;
        /* A window that ends earlier than it starts runs past midnight
           (e.g. "10:00 PM - 12:00 AM"); it has not elapsed today. */
        if (start !== null && end <= start) return end + 1440;
        return end;
      }

      syncWindowAvailability() {
        const now = this.nowInStoreTz();
        const isToday = this.dateInput.value === now.date;
        let clearedSelection = false;
        for (const option of this.windowInput.options) {
          if (!option.value) continue;
          const end = this.windowEndMinutes(option.value);
          const passed = isToday && end !== null && end <= now.minutes;
          option.disabled = passed;
          option.hidden = passed;
          if (passed && this.windowInput.value === option.value) {
            this.windowInput.value = '';
            clearedSelection = true;
          }
        }
        if (clearedSelection) {
          this.toggleError(this.windowError, true);
        }
      }

      /* ---- validation (Dawn error patterns, no alert()) ---- */

      toggleError(el, show, text) {
        if (!el) return;
        el.hidden = !show;
        if (text && this.dateErrorText && el === this.dateError) this.dateErrorText.textContent = text;
      }

      validateDate(showErrors) {
        const value = this.dateInput.value;
        let error = null;
        if (!value) {
          error = this.config.strings.dateRequired;
        } else if (this.isBlackout(value)) {
          error = this.config.strings.dateBlackout;
        } else if (value < this.minDate || value > this.maxDate) {
          error = this.config.strings.dateOutOfRange;
        }
        if (showErrors) {
          this.toggleError(this.dateError, Boolean(error), error);
          this.dateInput.setAttribute('aria-invalid', error ? 'true' : 'false');
        }
        return !error;
      }

      validateWindow(showErrors) {
        this.syncWindowAvailability();
        const valid = this.windowInput.value !== '';
        if (showErrors) {
          this.toggleError(this.windowError, !valid);
          this.windowInput.setAttribute('aria-invalid', valid ? 'false' : 'true');
        }
        return valid;
      }

      onFormSubmit(event) {
        const dateOk = this.validateDate(true);
        const windowOk = this.validateWindow(true);
        if (!dateOk || !windowOk) {
          event.preventDefault();
          event.stopImmediatePropagation();
          const firstInvalid = dateOk ? this.windowInput : this.dateInput;
          firstInvalid.focus();
          firstInvalid.scrollIntoView({ block: 'center', behavior: 'smooth' });
          return;
        }
        if (this.hasScheduleConflict()) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.showConflict();
        }
      }

      /* ---- one delivery per order (V1 invariant) ----
         Date + window are persisted per line item; a cart must not mix
         schedules. Destination (ZIP) is NOT persisted per line, so
         destination equality cannot be validated here — checkout's
         single delivery address is the authority on destination. */

      fetchCartSchedule() {
        return fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { cache: 'no-store' })
          .then((response) => response.json())
          .then((cart) => {
            let found = null;
            for (const item of cart.items || []) {
              const date = item.properties && item.properties['Delivery Date'];
              const time = item.properties && item.properties['Delivery Time'];
              if (date) {
                found = { date, time: time || '' };
                break;
              }
            }
            this.cartSchedule = found;
          })
          .catch(() => {
            /* fail open: never block ordering on a failed fetch */
          });
      }

      hasScheduleConflict() {
        if (!this.cartSchedule) return false;
        return (
          this.dateInput.value !== this.cartSchedule.date ||
          this.windowInput.value !== this.cartSchedule.time
        );
      }

      showConflict() {
        if (!this.conflictEl) return;
        const text = this.querySelector('[data-vip-conflict-text]');
        if (text) {
          text.textContent = this.config.strings.scheduleConflict
            .replace('[date]', this.formatConflictDate(this.cartSchedule.date))
            .replace('[window]', this.cartSchedule.time);
        }
        this.conflictEl.hidden = false;
        this.conflictEl.focus();
      }

      formatConflictDate(iso) {
        const d = new Date(`${iso}T12:00:00Z`);
        if (Number.isNaN(d.getTime())) return iso;
        return d.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric', timeZone: 'UTC' });
      }

      adoptCartSchedule() {
        if (!this.cartSchedule) return;
        this.dateInput.value = this.cartSchedule.date;
        this.syncWindowAvailability();
        this.windowInput.value = this.cartSchedule.time;
        this.conflictEl.hidden = true;
        this.validateDate(true);
        this.validateWindow(true);
        /* Keep the shared browsing date in step with the cart, so the
           collection bar and "receive on" chips stop advertising a day
           the order can no longer be delivered on. */
        this.saveDate(this.cartSchedule.date);
        this.dateInput.focus();
      }

      /* ---- ZIP checker (customer-facing mirror; checkout is authoritative) ---- */

      /* Prefill from the collection bar's picked date (shared key);
         only when valid under this product's bounds, never overriding
         a value the customer already set here. */
      prefillSavedDate() {
        if (this.dateInput.value) return;
        let saved = null;
        try {
          saved = localStorage.getItem('vip:delivery-date');
        } catch {
          return;
        }
        if (saved && saved >= this.minDate && saved <= this.maxDate && !this.isBlackout(saved)) {
          this.dateInput.value = saved;
        }
      }

      saveDate(iso) {
        try {
          localStorage.setItem('vip:delivery-date', iso);
        } catch {
          /* non-essential */
        }
      }

      readSavedZip() {
        try {
          return localStorage.getItem('vip:delivery-zip');
        } catch {
          return null;
        }
      }

      saveZip(zip) {
        try {
          localStorage.setItem('vip:delivery-zip', zip);
        } catch {
          /* storage unavailable — non-essential */
        }
      }

      formatMoney(amount) {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: this.config.currency || 'USD',
          minimumFractionDigits: Number(amount) % 1 === 0 ? 0 : 2,
        }).format(Number(amount));
      }

      findZone(zip) {
        return this.config.zones.find((zone) => (zone.zips || []).includes(zip)) || null;
      }

      checkZip(silent) {
        const zip = (this.zipInput.value || '').trim();
        if (!/^\d{5}$/.test(zip)) {
          if (!silent) this.renderZipResult(`<p class="vip-delivery__zip-message">${this.config.strings.zipInvalid}</p>`, 'invalid');
          return;
        }
        this.saveZip(zip);
        const zone = this.findZone(zip);
        if (!this.config.zones.length) {
          this.zipResult.textContent = '';
          return;
        }
        if (!zone) {
          this.renderZipResult(
            `<p class="vip-delivery__zip-message">${this.config.strings.zipUnserved.replace('[zip]', zip)}</p>`,
            'unserved'
          );
          document.dispatchEvent(new CustomEvent('vip:zip:change', { detail: { zip, zone: null } }));
          return;
        }
        const lines = [];
        lines.push(`<p class="vip-delivery__zip-message">${this.config.strings.zipServed.replace('[zip]', zip)}</p>`);
        const feeText =
          Number(zone.fee) > 0
            ? this.config.strings.feeLine.replace('[fee]', this.formatMoney(zone.fee))
            : null;
        const freeText = zone.freeThreshold
          ? this.config.strings.freeOver.replace('[amount]', this.formatMoney(zone.freeThreshold))
          : null;
        const detail = [feeText, freeText].filter(Boolean).join(' · ');
        if (detail) lines.push(`<p class="vip-delivery__zip-detail">${detail}</p>`);
        if (zone.minOrder && Number(zone.minOrder) > 0) {
          lines.push(
            `<p class="vip-delivery__zip-detail">${this.config.strings.minOrderNote.replace(
              '[amount]',
              this.formatMoney(zone.minOrder)
            )}</p>`
          );
        }
        this.renderZipResult(lines.join(''), 'served');
        document.dispatchEvent(new CustomEvent('vip:zip:change', { detail: { zip, zone } }));
      }

      renderZipResult(html, state) {
        this.zipResult.innerHTML = html;
        this.zipResult.dataset.state = state;
      }
    }
  );
}
